import { useState, useEffect } from 'react';
import categoryService from '../services/categoryService';
import { formatCategoryInfo } from '../utils/categoryUtils';
import '../styles/matches.css'; // Reutilizamos los estilos de matches

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, _setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filtros
  const [genderFilter, setGenderFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [birthYearFilter, setBirthYearFilter] = useState('all'); // NUEVO filtro
  const [favoriteCategories, setFavoriteCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        // Obtener las categorías activas del backend
        const categoriesData = await categoryService.getCategories({ active: true });
        console.log('Categorías obtenidas del backend:', categoriesData);

        // Transformar los datos usando la utilidad de formateo
        const formattedCategories = categoriesData.map((category) => {
          const formatted = formatCategoryInfo(category);
          return {
            ...formatted,
            playersPerTeam: category.playersPerTeam,
            fieldSize: category.fieldSize,
            matchDuration: category.matchDuration,
            // Generar imagen basada en el género y edad/año
            categoryImage: `/img/category-${category.gender}-${category.ageRange?.min || category.birthYearRange?.start || 6}.svg`,
          };
        });

        setCategories(formattedCategories);
        setTotalPages(Math.ceil(formattedCategories.length / 6)); // 6 categorías por página
        setLoading(false);
      } catch (error) {
        console.error('Error fetching categories:', error);

        // Si hay un error, usar datos de ejemplo como fallback
        const mockCategories = [
          {
            id: 'static-category-1',
            name: 'U8 Boys',
            description: 'Categoría para niños de 6 a 8 años. Partidos de 7 contra 7 en campo reducido.',
            ageRange: { min: 6, max: 8 },
            ageRangeText: '6-8 años',
            gender: 'boys',
            genderText: 'Niños',
            playersPerTeam: 7,
            fieldSize: 'small',
            matchDuration: 40,
            color: '#1976d2',
            icon: 'sports_soccer',
            active: true,
            categoryImage: '/img/category-boys-6.svg',
          },
          {
            id: 'static-category-2',
            name: 'U8 Girls',
            description: 'Categoría para niñas de 6 a 8 años. Partidos de 7 contra 7 en campo reducido.',
            ageRange: { min: 6, max: 8 },
            ageRangeText: '6-8 años',
            gender: 'girls',
            genderText: 'Niñas',
            playersPerTeam: 7,
            fieldSize: 'small',
            matchDuration: 40,
            color: '#e91e63',
            icon: 'sports_soccer',
            active: true,
            categoryImage: '/img/category-girls-6.svg',
          },
          {
            id: 'static-category-3',
            name: 'U10 Mixed',
            description: 'Categoría mixta para niños y niñas de 8 a 10 años. Partidos de 7 contra 7 en campo reducido.',
            ageRange: { min: 8, max: 10 },
            ageRangeText: '8-10 años',
            gender: 'mixed',
            genderText: 'Mixto',
            playersPerTeam: 7,
            fieldSize: 'medium',
            matchDuration: 50,
            color: '#4caf50',
            icon: 'sports_soccer',
            active: true,
            categoryImage: '/img/category-mixed-8.svg',
          },
        ];

        console.log('Usando datos de ejemplo como fallback');
        setCategories(mockCategories);
        setTotalPages(1);
        setLoading(false);
      }
    };

    fetchCategories();
  }, [currentPage, genderFilter, ageFilter, birthYearFilter]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Resetear a la primera página al aplicar filtros
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Función para alternar favoritos
  const toggleFavorite = (categoryId) => {
    if (favoriteCategories.includes(categoryId)) {
      setFavoriteCategories(favoriteCategories.filter((id) => id !== categoryId));
    } else {
      setFavoriteCategories([...favoriteCategories, categoryId]);
    }
  };

  // Filtrar categorías según los filtros seleccionados
  const filteredCategories = categories.filter((category) => {
    // Filtro por género
    if (genderFilter !== 'all' && category.gender !== genderFilter) {
      return false;
    }

    // Filtro por edad (mantener para compatibilidad)
    if (ageFilter !== 'all') {
      const ageGroup = ageFilter;
      const minAge = category.ageRange?.min || 0;

      if (ageGroup === 'u8' && minAge >= 8) return false;
      if (ageGroup === 'u10' && (minAge < 8 || minAge >= 10)) return false;
      if (ageGroup === 'u12' && minAge < 10) return false;
    }

    // NUEVO: Filtro por año de nacimiento
    if (birthYearFilter !== 'all') {
      const currentYear = new Date().getFullYear();
      let targetBirthYear;

      switch (birthYearFilter) {
        case '2018-2021': // 3-6 años
          targetBirthYear = 2019; // Año medio
          break;
        case '2014-2017': // 7-10 años
          targetBirthYear = 2015;
          break;
        case '2010-2013': // 11-14 años
          targetBirthYear = 2011;
          break;
        case '2006-2009': // 15-18 años
          targetBirthYear = 2007;
          break;
        default:
          return true;
      }

      // Verificar si la categoría incluye el año objetivo
      if (category.birthYearRange) {
        return targetBirthYear >= category.birthYearRange.start &&
               targetBirthYear <= category.birthYearRange.end;
      } else if (category.ageRange) {
        // Fallback a lógica de edad si no hay birthYearRange
        const age = currentYear - targetBirthYear;
        return age >= category.ageRange.min && age <= category.ageRange.max;
      }
    }

    return true;
  });

  if (loading) {
    return (
      <div className="container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando categorías...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="page-header">
        <div className="container">
          <h1>Categorías</h1>
          <p>Descubre las diferentes categorías de Liga Prokids</p>
        </div>
      </section>

      <section className="matches-section">
        <div className="container">
          <div className="filters">
            <div className="filter-group">
              <label htmlFor="gender-filter">Género</label>
              <select
                id="gender-filter"
                className="filter-select"
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
              >
                <option value="all">Todos los géneros</option>
                <option value="boys">Niños</option>
                <option value="girls">Niñas</option>
                <option value="mixed">Mixto</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="age-filter">Edad</label>
              <select
                id="age-filter"
                className="filter-select"
                value={ageFilter}
                onChange={(e) => setAgeFilter(e.target.value)}
              >
                <option value="all">Todas las edades</option>
                <option value="u8">Sub-8 (6-8 años)</option>
                <option value="u10">Sub-10 (8-10 años)</option>
                <option value="u12">Sub-12 (10-12 años)</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="birth-year-filter">Año de Nacimiento</label>
              <select
                id="birth-year-filter"
                className="filter-select"
                value={birthYearFilter}
                onChange={(e) => setBirthYearFilter(e.target.value)}
              >
                <option value="all">Todos los años</option>
                <option value="2018-2021">2018-2021 (3-6 años)</option>
                <option value="2014-2017">2014-2017 (7-10 años)</option>
                <option value="2010-2013">2010-2013 (11-14 años)</option>
                <option value="2006-2009">2006-2009 (15-18 años)</option>
              </select>
            </div>
            <button id="apply-filters" className="btn btn-primary" onClick={handleApplyFilters}>
              Aplicar Filtros
            </button>
          </div>

          {categories.length === 0 ? (
            <div className="no-matches">
              <span className="material-icons">category</span>
              <h3>No hay categorías disponibles</h3>
              <p>No se encontraron categorías que coincidan con los filtros seleccionados.</p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setGenderFilter('all');
                  setAgeFilter('all');
                  setBirthYearFilter('all');
                }}
              >
                Limpiar Filtros
              </button>
            </div>
          ) : (
            <>
              <div className="matches-grid" id="categories-container">
                {filteredCategories.map((category) => (
                  <div
                    className="match-card"
                    key={category.id}
                    style={{ borderTop: `4px solid ${category.color}` }}
                  >
                    <div className="match-header">
                      <div className="match-date">
                        <span className="material-icons" style={{ color: category.color }}>
                          {category.icon}
                        </span>
                        {category.rangeText}
                      </div>
                      <button
                        className="favorite-btn"
                        onClick={() => toggleFavorite(category.id)}
                        aria-label={
                          favoriteCategories.includes(category.id)
                            ? 'Quitar de favoritos'
                            : 'Añadir a favoritos'
                        }
                      >
                        <span className="material-icons">
                          {favoriteCategories.includes(category.id) ? 'favorite' : 'favorite_border'}
                        </span>
                      </button>
                    </div>

                    <div className="match-image">
                      <img
                        src={category.categoryImage}
                        alt={category.name}
                        onError={(e) => {
                          e.target.src = '/img/1x/icon-team.png';
                        }}
                      />
                    </div>

                    <div className="match-content">
                      <h3 className="match-title">{category.name}</h3>
                      <p className="category-description">{category.description}</p>

                      <div className="match-details">
                        <div className="match-category">
                          <span className="material-icons">group</span>
                          {category.genderText}
                        </div>

                        <div className="match-time">
                          <span className="material-icons">people</span>
                          {category.playersPerTeam} jugadores por equipo
                        </div>

                        <div className="match-location">
                          <span className="material-icons">schedule</span>
                          {category.matchDuration} minutos por partido
                        </div>

                        <div className="match-spots">
                          <span className="material-icons">sports</span>
                          Campo {category.fieldSize === 'small' ? 'reducido' :
                                 category.fieldSize === 'medium' ? 'mediano' : 'completo'}
                        </div>
                      </div>
                    </div>

                    <div className="match-actions">
                      <button className="btn btn-secondary">
                        Más Info
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <span className="material-icons">chevron_left</span>
                </button>
                {[...Array(totalPages).keys()].map((page) => (
                  <button
                    key={page + 1}
                    className={`pagination-btn ${currentPage === page + 1 ? 'active' : ''}`}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    {page + 1}
                  </button>
                ))}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <span className="material-icons">chevron_right</span>
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Categories;
